// Theme Configuration - Light & Dark Mode
import { useState, useEffect, createContext, useContext } from 'react';

// Theme colors
export const THEMES = {
    light: {
        name: 'light',
        colors: {
            bg: '#F5F5F7',
            card: '#FFFFFF',
            secondary: '#E5E5EA',
            tertiary: '#D1D1D6',
            textPrimary: '#1C1C1E',
            textSecondary: '#636366',
            textTertiary: '#8E8E93',
        }
    },
    dark: {
        name: 'dark',
        colors: {
            bg: '#000000',
            card: '#1C1C1E',
            secondary: '#2C2C2E',
            tertiary: '#3A3A3C',
            textPrimary: '#FFFFFF',
            textSecondary: '#A1A1A6',
            textTertiary: '#636366',
        }
    }
};

// Theme Context
const ThemeContext = createContext();

export function ThemeProvider({ children }) {
    const [theme, setTheme] = useState(() => {
        const saved = localStorage.getItem('app_theme');
        return saved || 'dark';
    });

    useEffect(() => {
        localStorage.setItem('app_theme', theme);
        // Apply theme class to root
        document.documentElement.classList.remove('light', 'dark');
        document.documentElement.classList.add(theme);

        // Update theme-color meta tag for status bar color
        const themeColor = theme === 'dark' ? '#0A0A0A' : '#F2F2F7';
        let metaThemeColor = document.querySelector('meta[name="theme-color"]');
        if (metaThemeColor) {
            metaThemeColor.setAttribute('content', themeColor);
        } else {
            metaThemeColor = document.createElement('meta');
            metaThemeColor.name = 'theme-color';
            metaThemeColor.content = themeColor;
            document.head.appendChild(metaThemeColor);
        }

        // Also update apple-mobile-web-app-status-bar-style
        const statusBarStyle = theme === 'dark' ? 'black-translucent' : 'default';
        let metaStatusBar = document.querySelector('meta[name="apple-mobile-web-app-status-bar-style"]');
        if (metaStatusBar) {
            metaStatusBar.setAttribute('content', statusBarStyle);
        }
    }, [theme]);

    const toggleTheme = () => {
        setTheme(prev => prev === 'dark' ? 'light' : 'dark');
    };

    const value = {
        theme,
        setTheme,
        toggleTheme,
        isDark: theme === 'dark',
        colors: THEMES[theme].colors,
    };

    return (
        <ThemeContext.Provider value={value}>
            {children}
        </ThemeContext.Provider>
    );
}

export function useTheme() {
    const context = useContext(ThemeContext);
    if (!context) {
        throw new Error('useTheme must be used within ThemeProvider');
    }
    return context;
}

export default { THEMES, ThemeProvider, useTheme };
